/**
 * Embeds the TurboTwin platform Bevy viewer (served same-origin under `/bevy`
 * — see `serveBevyPlugin` in `vite.config.ts`) inside the Orientation Editor's
 * map slot, as the "3D" alternative to the 2D Leaflet `StepMap`.
 *
 * P0 is read-only: it renders the selected site's 3D world (terrain basemap +
 * twins) so an author can see the ground truth. Camera fly-to-a-stop and
 * "capture this framing" authoring arrive in P2, once the platform ships the
 * geo-camera bridge messages ("E7"). See
 * `MineTwinMonoRepo/plans/01-draft/ORIENTATION-EDITOR-3D-VIEWER-PLAN.md`.
 *
 * The viewer owns rendering; this host owns auth (WorkOS token handoff) and
 * project selection. Same-origin serving means the trusted postMessage origin
 * is our own — the viewer rejects any other parent, and we reject any other
 * source.
 *
 * Version dependency: `/bevy` is a COPY of the platform viewer, staged from
 * `minetwin.turbotwin.cloud/bevy` by `deploy-admin-portal.yml` at build time.
 * Any new bridge message this host sends (e.g. `flyCam`, ordered `ghostMarkers`)
 * OR viewer-side rendering change only takes effect once BOTH the viewer is
 * published to that CDN AND the portal redeploys to re-fetch it — a portal
 * deploy that runs first ships the old bundle, so re-run the deploy (this
 * workflow only fires on `admin-portal/**` changes) after the viewer lands.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useAuthKit } from '@/contexts/AuthProvider';
import { useOrgSession } from '@/contexts/OrgContext';
import type { InboundMessage, OutboundMessage, TwinInfoField } from './viewerProtocol';
import { isTransientViewerError } from './viewerProtocol';
import type { CameraParamsWire } from './cameraModes';

/** The E7 `cameraPose` reply shape, derived from the outbound union. */
export type CameraPose = Extract<OutboundMessage, { type: 'cameraPose' }>;
/** `flyToGeo` payload minus the discriminant — what callers pass in. */
export type FlyToGeoArgs = Omit<Extract<InboundMessage, { type: 'flyToGeo' }>, 'type'>;
/** `flyAlongPath` payload minus the discriminant — multi-location preview args. */
export type FlyAlongPathArgs = Omit<Extract<InboundMessage, { type: 'flyAlongPath' }>, 'type'>;
/** `setOverlay` payload minus the discriminant — persistent line/polygon/point. */
export type SetOverlayArgs = Omit<Extract<InboundMessage, { type: 'setOverlay' }>, 'type'>;
/** Cinematic-verb payloads (v7), minus the discriminant — preview call args. */
export type FlyToTwinArgs = Omit<Extract<InboundMessage, { type: 'flyToTwin' }>, 'type'>;
export type LookAtTwinArgs = Omit<Extract<InboundMessage, { type: 'lookAtTwin' }>, 'type'>;
export type LookAtGeoArgs = Omit<Extract<InboundMessage, { type: 'lookAtGeo' }>, 'type'>;
export type FlyToTwinLookAtTwinArgs = Omit<
  Extract<InboundMessage, { type: 'flyToTwinLookAtTwin' }>,
  'type'
>;
export type FlyToLookAtArgs = Omit<Extract<InboundMessage, { type: 'flyToLookAt' }>, 'type'>;
/** Continuous camera-motion payloads, minus the discriminant. */
export type OrbitTwinArgs = Omit<Extract<InboundMessage, { type: 'orbitTwin' }>, 'type'>;
export type FollowTwinArgs = Omit<Extract<InboundMessage, { type: 'followTwin' }>, 'type'>;
export type CameraModeName = Extract<InboundMessage, { type: 'setCameraMode' }>['mode'];
/** `measure` payload minus the discriminant (level 33). */
export type MeasureArgs = Omit<Extract<InboundMessage, { type: 'measure' }>, 'type'>;
/** The viewer's `measureChanged` report (level 33). */
export type MeasureChanged = Extract<OutboundMessage, { type: 'measureChanged' }>;

/** Imperative surface the Orientation Editor drives for P2 camera authoring.
 *  Fly-to-a-stop is declarative (the `focusGeo` prop); this handle carries the
 *  one-shot capture and the fire-and-forget cinematic-verb previews. */
export type ViewerPaneHandle = {
  /** Capture the current camera framing. Rejects if the viewer isn't ready or
   *  doesn't answer within `timeoutMs` (default 5000). */
  getCameraPose: (timeoutMs?: number) => Promise<CameraPose>;
  /** Preview an authored camera command in the embedded viewer. Fire-and-forget
   *  (the viewer sends no reply); a no-op if the viewer isn't ready. Returns
   *  whether the message was actually sent, so callers can surface "switch to
   *  3D / wait for load" feedback. */
  flyToGeo: (args: FlyToGeoArgs) => boolean;
  /** Preview a multi-location fly-along path (the camera rides the waypoints in
   *  order). No-op on a /bevy bundle that predates the platform `flyAlongPath`
   *  verb (bevy 8bd0190c73+). */
  flyAlongPath: (args: FlyAlongPathArgs) => boolean;
  /** Set the GLOBAL playback speed of all viewer camera motion — the Play-speed
   *  dropdown. `factorMilli` = multiplier × 1000 (1000 = normal, 2000 = 2×);
   *  global + persistent until changed. No-op on a /bevy bundle built before
   *  bevy protocol 24. The caller must scale the beat / narration timing by the
   *  same factor — this only paces what the viewer renders. */
  setPlaybackSpeed: (factorMilli: number) => boolean;
  /** Draw/replace a persistent overlay (line/polygon/point) by id — used to show
   *  an authored fly path as a line in 3D. `removeOverlay` clears it. */
  setOverlay: (args: SetOverlayArgs) => boolean;
  removeOverlay: (id: string) => boolean;
  flyToTwin: (args: FlyToTwinArgs) => boolean;
  lookAtTwin: (args: LookAtTwinArgs) => boolean;
  lookAtGeo: (args: LookAtGeoArgs) => boolean;
  flyToTwinLookAtTwin: (args: FlyToTwinLookAtTwinArgs) => boolean;
  /** Generalized Fly + look: fly/look each a twin or a geo coordinate. */
  flyToLookAt: (args: FlyToLookAtArgs) => boolean;
  /** Start a continuous orbit / follow (runs until `stopCamera`). */
  orbitTwin: (args: OrbitTwinArgs) => boolean;
  followTwin: (args: FollowTwinArgs) => boolean;
  /** Stop any continuous camera motion. */
  stopCamera: () => boolean;
  /** Force a camera mode (used to put the rig in Free Orbit for a continuous
   *  preview, then restore the author's mode on stop). */
  setCameraMode: (mode: CameraModeName) => boolean;
  /** Toggle one twin's relationship lines (shown=true adds it to the viewer's
   *  shown set, false removes it). Previews ShowRelationships/HideRelationships. */
  setRelationships: (twinId: string, shown: boolean) => boolean;
  /** Draw ALL relationship edges (the global show-all), independent of the
   *  per-twin toggle. Previews a Show/HideRelationships with no twins (= all). */
  setRelationshipLinesVisible: (visible: boolean) => boolean;
  /** Highlight a geo point — the viewer's pulsing "this location" marker, the
   *  same `highlightGeo` verb orientation playback fires for a Highlight effect.
   *  `null` clears it. Used to preview an authored HighlightTwin / HighlightGeo
   *  command (both resolve their twin to a point, exactly as playback does). */
  highlightGeo: (args: { latE7: number; lonE7: number; elevMilli: number } | null) => boolean;
  /** Second, GREEN geo marker (the Fly + look FLY point), shown alongside
   *  `highlightGeo` (orange). `null` clears it. */
  highlightFly: (args: { latE7: number; lonE7: number; elevMilli: number } | null) => boolean;
  /** Show/clear the interactive STOP MARKER (the tube the author flies into to
   *  start/advance a preview). `null` clears it. The viewer reports the camera
   *  entering/leaving via `onStopMarkerReached`/`onStopMarkerLeft`. */
  showStopMarker: (args: { latE7: number; lonE7: number; elevMilli: number } | null) => boolean;
  /** Highlight a twin at its ACTUAL viewer position (mesh centre / live entity
   *  position) — resolved viewer-side, so the cue lands where the twin renders
   *  rather than on its stored geo anchor. An empty id clears it. */
  highlightTwin: (twinId: string) => boolean;
  /** Absolute selection set — outline these twins ([] clears). Unlike `select`
   *  it never moves the camera. Previews SelectTwins / ClearSelection. */
  setSelection: (twinIds: string[]) => boolean;
  /** Fade a set of twins to `opacityMilli` (opacity × 1000; [] or >= 1000
   *  restores). Previews the SetTransparency beat. Material fade, not outline. */
  setTwinTransparency: (twinIds: string[], opacityMilli: number, dither?: boolean) => boolean;
  /** Show/refresh/clear the ShowTwinInfo panel for a twin. The viewer resolves
   *  the chosen property/telemetry VALUES + the twin's screen position and
   *  replies with `twinInfo`; this pane draws the card. `null` clears it. No-op
   *  on a /bevy bundle built before the viewer's showTwinInfo verb. */
  showTwinInfo: (
    args: { twinId: string; propertyNames: string[]; telemetryNames: string[] } | null,
  ) => boolean;
  /** Arm / disarm / re-configure the measure tool (level 33; an older viewer
   *  drops it, so gate on `onProtocolVersion`). */
  measure: (args: MeasureArgs) => boolean;
};

// Empty string is MEANINGFUL for the minetwin stamp: "no CDN rewrite — fetch
// presigned blob URLs directly". ABSENT (undefined) is different and wrong:
// the viewer would derive the house asset CDN and 404 every tile/GLB. So we
// always pass it explicitly. No-op in dev until asset-backed twins render.
const ASSETS_CDN_HOST =
  (import.meta.env.VITE_BEVY_ASSETS_CDN_HOST as string | undefined) ?? '';

const READY_TIMEOUT_MS = 60_000;
const TRANSIENT_ERROR_MS = 10_000;

type ViewerPaneProps = {
  /** The current site's project slug — the viewer opens `{org}-{slug}-core`. */
  projectSlug: string;
  /** Geodetic point the camera should look at (typically the selected stop).
   *  The pane flies there whenever this changes AND once more when the viewer
   *  first reports ready, so a stop selected during boot still lands the camera.
   *  null / undefined = don't move. Integer-encoded per the wire rule; memoize
   *  it on the caller side so a stable selection doesn't re-fly every render. */
  focusGeo?: FlyToGeoArgs | null;
  /** CSV of stop positions to render as ghost spike markers in 3D — one
   *  `latE7,lonE7,elevMilli` per line (empty string clears). Sent on change and
   *  on viewer-ready, same model as focusGeo. undefined = don't manage markers. */
  ghostMarkersCsv?: string;
  /** The viewer reported a twin selection (an author clicked a twin in 3D).
   *  Feeds the P2 "Target twin" round-trip; optional so P0 read-only callers
   *  can ignore it. */
  onSelected?: (twinId: string) => void;
  /** The viewer reported a twin DOUBLE-click (distinct from single-click
   *  `onSelected`). The World-selection check editor uses it to add the
   *  double-clicked twin to its expected set. */
  onTwinDoubleClicked?: (twinId: string) => void;
  /** The camera entered the active stop marker's tube — the interactive play loop
   *  treats it as "arrived at the stop" and shows the Start/Advance prompt. */
  onStopMarkerReached?: () => void;
  /** The camera left the stop marker's tube before confirming. */
  onStopMarkerLeft?: () => void;
  /** Arm the viewer's pick-point tool: while true, a click in the 3D scene
   *  raycasts terrain/objects and fires `onPointPicked` with the hit's real
   *  coordinate. Sent to the viewer on change and on ready; disarms (sends
   *  `enabled:false`) when set back to false. undefined = never arm. */
  pickMode?: boolean;
  /** A pick-point click landed on geometry — the snapped WGS84 coordinate WITH
   *  real elevation (a sky-click sends nothing, so this only fires on a hit).
   *  Only meaningful while `pickMode` is armed. */
  onPointPicked?: (lat: number, lon: number, elev: number) => void;
  /** Active camera mode (orientation editor's 3-mode camera): 'freeLook' (WASD
   *  drone + RMB look), 'freeOrbit' (RMB orbit a cursor pivot + MMB pan + wheel
   *  zoom), or 'walk' (grounded first-person). Sent on change and on ready. */
  cameraMode?: 'freeLook' | 'freeOrbit' | 'walk';
  /** Per-mode camera tuning (viewer protocol v8), integer-encoded. Sent with the
   *  mode on change and on ready; a pre-v8 dist ignores it. */
  cameraParams?: CameraParamsWire;
  /** Show/hide the viewer's twin tag markers. Sent on change and on ready;
   *  undefined = don't manage (viewer default: visible). */
  twinMarkersVisible?: boolean;
  /** Display units for the lengths the viewer prints. Sent on change and on
   *  ready; undefined = don't manage (viewer default: metric). */
  units?: 'metric' | 'imperial';
  /** Pulsing highlight marker at ONE geodetic point (the selected geo
   *  command's location) — integer E7/milli like flyToGeo. null clears it;
   *  undefined = don't manage. Sent on change and on ready; a pre-highlightGeo
   *  /bevy dist drops it silently. */
  highlightGeo?: { latE7: number; lonE7: number; elevMilli: number } | null;
  /** Second, GREEN marker (the Fly + look FLY location) shown alongside
   *  `highlightGeo`. null clears; undefined = don't manage. */
  highlightFly?: { latE7: number; lonE7: number; elevMilli: number } | null;
  /** Pulsing glow on the twin a selected command targets (the twin counterpart
   *  to `highlightGeo`). A twin id highlights it; '' clears; undefined = don't
   *  manage. Sent on change and on ready. */
  highlightTwin?: string;
  /** Persistent overlays for the selected "Fly along path" command — the route
   *  LINE plus START/END point markers — so the path is visible in 3D. null (or
   *  []) clears them; undefined = don't manage. Sent on change and on ready (a
   *  pre-level-12 /bevy dist drops them silently). */
  pathOverlay?: SetOverlayArgs[] | null;
  /** The viewer tuned a camera param itself (Free Look speed via +/- or
   *  Shift+wheel) — mm/s, same encoding the host authors. Update the camera
   *  UI so it stays truthful. */
  onCameraParamChanged?: (mode: string, freeLookSpeedMm: number) => void;
  /** The viewer's protocol level from `ready` (0 for a dist that predates the
   *  field), and 0 again while it reboots for a new project. Feature gates. */
  onProtocolVersion?: (version: number) => void;
  /** The armed measure tool reported a change (`measureChanged`, level 33). */
  onMeasureChanged?: (msg: MeasureChanged) => void;
};

export const ViewerPane = forwardRef<ViewerPaneHandle, ViewerPaneProps>(function ViewerPane(
  { projectSlug, focusGeo, ghostMarkersCsv, onSelected, onTwinDoubleClicked, onStopMarkerReached, onStopMarkerLeft, pickMode, onPointPicked, cameraMode, cameraParams, twinMarkersVisible, units, highlightGeo, highlightFly, highlightTwin, pathOverlay, onCameraParamChanged, onProtocolVersion, onMeasureChanged },
  ref,
) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { user, getAccessToken } = useAuthKit();
  const { org } = useOrgSession();
  const orgSlug = org.slug;
  const [ready, setReady] = useState(false);
  const [bootPhase, setBootPhase] = useState<string | null>(null);
  const [bootCurrent, setBootCurrent] = useState(0);
  const [bootTotal, setBootTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Same-origin serving → the only trusted postMessage origin is our own.
  const expectedOrigin = window.location.origin;
  // Dedup guard so a rotation re-send only posts when the token actually changed.
  const lastSentTokenRef = useRef<string | null>(null);

  // Keep the latest onSelected without re-subscribing the message listener.
  const onSelectedRef = useRef(onSelected);
  onSelectedRef.current = onSelected;
  const onTwinDoubleClickedRef = useRef(onTwinDoubleClicked);
  onTwinDoubleClickedRef.current = onTwinDoubleClicked;
  const onStopMarkerReachedRef = useRef(onStopMarkerReached);
  onStopMarkerReachedRef.current = onStopMarkerReached;
  const onStopMarkerLeftRef = useRef(onStopMarkerLeft);
  onStopMarkerLeftRef.current = onStopMarkerLeft;

  // Same for onPointPicked — the message listener reads it through the ref so a
  // changing callback identity doesn't re-subscribe.
  const onPointPickedRef = useRef(onPointPicked);
  onPointPickedRef.current = onPointPicked;
  const onCameraParamChangedRef = useRef(onCameraParamChanged);
  onCameraParamChangedRef.current = onCameraParamChanged;
  const onProtocolVersionRef = useRef(onProtocolVersion);
  onProtocolVersionRef.current = onProtocolVersion;
  const onMeasureChangedRef = useRef(onMeasureChanged);
  onMeasureChangedRef.current = onMeasureChanged;

  // In-flight getCameraPose() calls, keyed by requestId → their promise
  // settlers (the viewer's reply is correlated, not broadcast).
  const pendingPosesRef = useRef(
    new Map<
      string,
      { resolve: (p: CameraPose) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }
    >(),
  );
  const poseSeqRef = useRef(0);

  // ShowTwinInfo panel: the viewer resolves values + screen position and sends
  // `twinInfo`; we draw the card here. Cleared by `twinInfoEnd`.
  const [twinInfo, setTwinInfo] = useState<Extract<OutboundMessage, { type: 'twinInfo' }> | null>(
    null,
  );

  const send = useCallback(
    (msg: InboundMessage) => {
      iframeRef.current?.contentWindow?.postMessage(msg, expectedOrigin);
    },
    [expectedOrigin],
  );

  // Inbound viewer messages. Triple guard: origin, source, shape.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!event.data || typeof event.data !== 'object') return;
      const msg = event.data as OutboundMessage;
      switch (msg.type) {
        case 'ready':
          setReady(true);
          onProtocolVersionRef.current?.(msg.protocolVersion ?? 0);
          break;
        case 'measureChanged':
          onMeasureChangedRef.current?.(msg);
          break;
        case 'boot': {
          setBootPhase(msg.phase);
          const cur = msg.current ?? msg.loaded;
          if (typeof cur === 'number') setBootCurrent(cur);
          if (typeof msg.total === 'number') setBootTotal(msg.total);
          break;
        }
        case 'error':
          // Surface post-ready errors too (studio's ViewerFrame only showed
          // them pre-boot, so a mid-session viewer error went to console only).
          console.error('[viewer]', msg.message);
          setErrorMsg(msg.message);
          break;
        case 'twinDoubleClicked':
          // Author double-clicked a twin — the World-selection check adds it
          // to its expected set.
          onTwinDoubleClickedRef.current?.(msg.twinId);
          break;
        case 'stopMarkerReached':
          onStopMarkerReachedRef.current?.();
          break;
        case 'stopMarkerLeft':
          onStopMarkerLeftRef.current?.();
          break;
        case 'selected':
          // P2 selection round-trip: an author clicked a twin in the 3D scene.
          onSelectedRef.current?.(msg.twinId);
          break;
        case 'pointPicked':
          // Pick-point tool hit geometry — hand the host the real coordinate to
          // place/move a stop. (Only armed callers get here; a sky-click is
          // silent viewer-side, so there's nothing to filter.)
          onPointPickedRef.current?.(msg.lat, msg.lon, msg.elev);
          break;
        case 'cameraParamChanged':
          // The viewer tuned a camera param itself (Shift+wheel speed) — keep
          // the host's Camera Controls slider truthful.
          onCameraParamChangedRef.current?.(msg.mode, msg.freeLookSpeedMm);
          break;
        case 'cameraPose': {
          // Correlated reply to a getCameraPose() call — settle its promise.
          const pending = pendingPosesRef.current.get(msg.requestId);
          if (pending) {
            clearTimeout(pending.timer);
            pendingPosesRef.current.delete(msg.requestId);
            pending.resolve(msg);
          }
          break;
        }
        case 'twinInfo':
          setTwinInfo(msg);
          break;
        case 'twinInfoEnd':
          setTwinInfo(null);
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [expectedOrigin]);

  // Reset boot/error state when the project changes (the iframe remounts via
  // its key, so the viewer reboots into the new project).
  useEffect(() => {
    setReady(false);
    onProtocolVersionRef.current?.(0);
    setBootPhase(null);
    setErrorMsg(null);
    lastSentTokenRef.current = null;
    // Any capture in flight belonged to the previous project's viewer; the
    // iframe is remounting, so its reply will never come.
    const pending = pendingPosesRef.current;
    pending.forEach((p) => {
      clearTimeout(p.timer);
      p.reject(new Error('viewer reloaded'));
    });
    pending.clear();
  }, [projectSlug]);

  // Initial handshake: once the viewer says `ready`, hand it the WorkOS token
  // and boot Project mode. orgId is the derived org slug ('minetwin-default'),
  // projectId the selected site's slug — both match the `^[a-z0-9-]+$` grammar
  // the viewer validates.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    getAccessToken()
      .then((token) => {
        if (cancelled || !token) return;
        lastSentTokenRef.current = token;
        send({ type: 'auth', token, orgId: orgSlug, projectId: projectSlug });
        send({ type: 'mode', mode: 'project' });
      })
      .catch((e) => console.error('[viewer] auth token fetch failed', e));
    return () => {
      cancelled = true;
    };
  }, [ready, projectSlug, orgSlug, getAccessToken, send]);

  // Token-rotation re-send (the gap studio left open). An embedded viewer can't
  // run its own OIDC flow, so when its injected JWT expires its STDB sockets
  // die and never recover — unless the host re-injects a fresh token. We
  // re-post `auth` on a `user` change and on tab re-focus; cache-first
  // getAccessToken() makes the focus path cheap, and the dedup ref means we
  // only re-post when the token string actually rotated.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const resendIfRotated = () => {
      getAccessToken()
        .then((token) => {
          if (cancelled || !token || token === lastSentTokenRef.current) return;
          lastSentTokenRef.current = token;
          send({ type: 'auth', token, orgId: orgSlug, projectId: projectSlug });
        })
        .catch(() => {
          /* keep the existing token — a refresh blip shouldn't blank auth */
        });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') resendIfRotated();
    };
    document.addEventListener('visibilitychange', onVisible);
    resendIfRotated(); // also covers this effect re-running on a `user` change
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ready, user, projectSlug, orgSlug, getAccessToken, send]);

  // Watchdog: if the viewer never reports `ready` (e.g. the /bevy dist is
  // missing and the middleware served its 503 into the iframe), don't spin the
  // "Loading…" overlay forever — surface an actionable message.
  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => {
      setErrorMsg(
        'The 3D viewer did not start within 60s. In dev, run `pnpm bevy:fetch` to download the viewer bundle (or point BEVY_DIST_DIR at your own).',
      );
    }, READY_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [ready, projectSlug]);

  // A dropped module socket heals inside the viewer, which never reports the
  // recovery, so its banner clears itself instead of latching until reload.
  useEffect(() => {
    if (!errorMsg || !isTransientViewerError(errorMsg)) return;
    const t = setTimeout(() => setErrorMsg(null), TRANSIENT_ERROR_MS);
    return () => clearTimeout(t);
  }, [errorMsg]);

  // Fly-to-focus (P2.1): send `flyToGeo` when the host's target changes, and
  // again the moment the viewer reports ready — so a stop selected during boot
  // isn't lost. The viewer's PendingGeoFocus retries internally until the
  // project origin resolves, so an early send is safe.
  useEffect(() => {
    if (!ready || !focusGeo) return;
    send({ type: 'flyToGeo', ...focusGeo });
  }, [ready, focusGeo, send]);

  // Ghost markers (P2): render the orientation's stops as spike meshes in 3D.
  // Same declarative model as focusGeo — resend on change and on ready. Each
  // message replaces the whole set; '' clears. undefined = caller isn't
  // managing markers, so leave whatever the viewer has.
  useEffect(() => {
    if (!ready || ghostMarkersCsv == null) return;
    // ordered:true — these are an orientation's stops in tour order, so the
    // viewer draws the numbered follow-line, not a flat import-preview set.
    send({ type: 'ghostMarkers', data: ghostMarkersCsv, ordered: true });
  }, [ready, ghostMarkersCsv, send]);

  // Pick-point arm/disarm: the tool is absolute host-owned state, so we mirror
  // `pickMode` to the viewer on change and on ready (arming a stop-placement
  // that was toggled on during boot). Sending `enabled:false` is a harmless
  // no-op on a fresh viewer, so no first-mount special-case is needed.
  useEffect(() => {
    if (!ready) return;
    send({ type: 'pickPoint', enabled: !!pickMode });
  }, [ready, pickMode, send]);

  // Camera mode + per-mode params — sent on change and on ready. undefined mode
  // = leave the viewer's default (Free Orbit). params re-send on every slider
  // change; the viewer applies them even when the mode is unchanged (v8). A
  // pre-v8 dist ignores `params` and just switches mode.
  useEffect(() => {
    if (!ready || !cameraMode) return;
    send({ type: 'setCameraMode', mode: cameraMode, params: cameraParams });
  }, [ready, cameraMode, cameraParams, send]);

  // Twin tag-marker visibility — sent on change and on ready. undefined = don't
  // manage (viewer default: visible). A dist without the message drops it.
  useEffect(() => {
    if (!ready || twinMarkersVisible === undefined) return;
    send({ type: 'setTagMarkersVisible', visible: twinMarkersVisible });
  }, [ready, twinMarkersVisible, send]);

  // Display units: same declarative model. A pre-level-32 /bevy drops it.
  useEffect(() => {
    if (!ready || units === undefined) return;
    send({ type: 'setUnits', system: units });
  }, [ready, units, send]);

  // Highlight marker — same declarative model: send on change and on ready;
  // null clears (a bare highlightGeo message = clear per the protocol).
  useEffect(() => {
    if (!ready || highlightGeo === undefined) return;
    send(highlightGeo ? { type: 'highlightGeo', ...highlightGeo } : { type: 'highlightGeo' });
  }, [ready, highlightGeo?.latE7, highlightGeo?.lonE7, highlightGeo?.elevMilli, send]); // eslint-disable-line react-hooks/exhaustive-deps

  // Green fly marker — same declarative model as highlightGeo, its own channel.
  useEffect(() => {
    if (!ready || highlightFly === undefined) return;
    send(highlightFly ? { type: 'highlightFly', ...highlightFly } : { type: 'highlightFly' });
  }, [ready, highlightFly?.latE7, highlightFly?.lonE7, highlightFly?.elevMilli, send]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fly-along-path line overlay — declarative like highlightGeo: send on change
  // and on ready; null removes it. Keyed on a JSON of the coords so an identical
  // path from a re-render doesn't re-send. undefined = don't manage.
  const pathOverlayJson = pathOverlay === undefined ? undefined : JSON.stringify(pathOverlay);
  useEffect(() => {
    if (!ready || pathOverlayJson === undefined) return;
    const ovs = (JSON.parse(pathOverlayJson) as SetOverlayArgs[] | null) ?? [];
    const ids = ['oe-fly-path', 'oe-fly-path-start', 'oe-fly-path-end'];
    for (const ov of ovs) send({ type: 'setOverlay', ...ov });
    // Remove any of the known overlays not in the current set (e.g. all three
    // when the selection leaves a path command, or the markers at <2 points).
    const present = new Set(ovs.map((o) => o.id));
    for (const id of ids) if (!present.has(id)) send({ type: 'removeOverlay', id });
  }, [ready, pathOverlayJson, send]);

  // Twin glow — the twin counterpart of highlightGeo: send on change and on
  // ready; '' clears the glow (the viewer clears on an empty twinId).
  useEffect(() => {
    if (!ready || highlightTwin === undefined) return;
    send({ type: 'highlightTwin', twinId: highlightTwin });
  }, [ready, highlightTwin, send]);

  // Reject any capture still in flight if the pane unmounts (e.g. toggle back
  // to 2D), so callers' awaits don't hang forever.
  useEffect(() => {
    const pending = pendingPosesRef.current;
    return () => {
      pending.forEach((p) => {
        clearTimeout(p.timer);
        p.reject(new Error('viewer unmounted'));
      });
      pending.clear();
    };
  }, []);

  // Imperative surface for the editor (P2): getCameraPose is a correlated
  // request/response surfaced as a Promise. (Fly-to is the declarative
  // `focusGeo` prop above, not part of this handle.)
  useImperativeHandle(
    ref,
    () => {
      // Fire-and-forget preview: only send once the viewer is ready (a message
      // posted into a booting viewer is dropped, not queued). Return whether it
      // went out so the caller can toast "switch to 3D / still loading".
      const fire = (msg: InboundMessage): boolean => {
        if (!ready) return false;
        send(msg);
        return true;
      };
      return {
        getCameraPose: (timeoutMs = 5000) =>
          new Promise<CameraPose>((resolve, reject) => {
            if (!ready) {
              reject(new Error('viewer not ready'));
              return;
            }
            const requestId = `pose-${poseSeqRef.current++}`;
            const timer = setTimeout(() => {
              pendingPosesRef.current.delete(requestId);
              reject(new Error('getCameraPose timed out'));
            }, timeoutMs);
            pendingPosesRef.current.set(requestId, { resolve, reject, timer });
            send({ type: 'getCameraPose', requestId });
          }),
        flyToGeo: (args) => fire({ type: 'flyToGeo', ...args }),
        flyAlongPath: (args) => fire({ type: 'flyAlongPath', ...args }),
        setPlaybackSpeed: (factorMilli) => fire({ type: 'setPlaybackSpeed', factorMilli }),
        setOverlay: (args) => fire({ type: 'setOverlay', ...args }),
        removeOverlay: (id) => fire({ type: 'removeOverlay', id }),
        flyToTwin: (args) => fire({ type: 'flyToTwin', ...args }),
        lookAtTwin: (args) => fire({ type: 'lookAtTwin', ...args }),
        lookAtGeo: (args) => fire({ type: 'lookAtGeo', ...args }),
        flyToTwinLookAtTwin: (args) => fire({ type: 'flyToTwinLookAtTwin', ...args }),
        flyToLookAt: (args) => fire({ type: 'flyToLookAt', ...args }),
        orbitTwin: (args) => fire({ type: 'orbitTwin', ...args }),
        followTwin: (args) => fire({ type: 'followTwin', ...args }),
        stopCamera: () => fire({ type: 'stopCamera' }),
        setCameraMode: (mode) => fire({ type: 'setCameraMode', mode }),
        setRelationships: (twinId, shown) => fire({ type: 'setRelationships', twinId, shown }),
        setRelationshipLinesVisible: (visible) => fire({ type: 'setRelationshipLinesVisible', visible }),
        highlightGeo: (args) =>
          fire(args ? { type: 'highlightGeo', ...args } : { type: 'highlightGeo' }),
        highlightFly: (args) =>
          fire(args ? { type: 'highlightFly', ...args } : { type: 'highlightFly' }),
        showStopMarker: (args) =>
          fire(args ? { type: 'showStopMarker', ...args } : { type: 'showStopMarker' }),
        highlightTwin: (twinId) => fire({ type: 'highlightTwin', twinId }),
        setSelection: (twinIds) => fire({ type: 'setSelection', twinIds }),
        setTwinTransparency: (twinIds, opacityMilli, dither) =>
          fire({ type: 'setTwinTransparency', twinIds, opacityMilli, dither }),
        showTwinInfo: (args) =>
          fire(
            args
              ? { type: 'showTwinInfo', ...args }
              : { type: 'showTwinInfo', twinId: '', propertyNames: [], telemetryNames: [] },
          ),
        measure: (args) => fire({ type: 'measure', ...args }),
      };
    },
    [ready, send],
  );

  const pct = bootTotal > 0 ? Math.min(100, Math.round((bootCurrent / bootTotal) * 100)) : null;

  return (
    <div className="oe-map" style={{ position: 'relative', background: 'var(--bg-1)' }}>
      <iframe
        key={projectSlug}
        ref={iframeRef}
        src={`/bevy/index.html?assetsCdnHost=${encodeURIComponent(ASSETS_CDN_HOST)}`}
        title="MineTwin 3D viewer"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        // `pointer-lock` lets Walk mode capture the mouse for first-person look
        // (the viewer requests it on a click inside the canvas).
        allow="cross-origin-isolated; pointer-lock"
      />
      {!ready && (
        <div className="oe-viewer-overlay">
          <div>
            {errorMsg
              ? `Viewer error: ${errorMsg}`
              : `Loading 3D viewer${bootPhase ? ` — ${bootPhase}` : '…'}`}
          </div>
          {pct !== null && !errorMsg && (
            <div className="oe-viewer-bar">
              <div style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
      )}
      {ready && errorMsg && <div className="oe-viewer-error-banner">{errorMsg}</div>}
      {ready && twinInfo && (
        <div
          className="oe-twin-info-card"
          style={{
            position: 'absolute',
            // Anchor the card's BOTTOM-CENTRE a bit above the marker: left/top
            // sit at the marker point (x, y − gap) and the translate pulls the
            // card left by half its width and up by its full height, so it
            // floats centred just above the twin.
            left: Math.round(twinInfo.x),
            top: Math.round(twinInfo.y) - 12,
            transform: 'translate(-50%, -100%)',
            zIndex: 3,
            pointerEvents: 'none',
            maxWidth: 280,
            background: 'var(--bg-2, #1b1f24)',
            color: 'var(--fg-1, #e6e6e6)',
            border: '1px solid var(--border, #3a3f46)',
            borderRadius: 6,
            padding: '8px 10px',
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            font: '12px/1.4 system-ui, sans-serif',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{twinInfo.name}</div>
          {twinInfo.fields.length === 0 ? (
            <div style={{ opacity: 0.6 }}>No properties or telemetry on this twin.</div>
          ) : (
            // Group by source into Properties / Telemetries; a group with no
            // rows is dropped together with its header.
            (['property', 'telemetry'] as const).map((group) => {
              const rows = twinInfo.fields.filter((f: TwinInfoField) => f.source === group);
              if (rows.length === 0) return null;
              return (
                <div key={group} style={{ marginTop: group === 'telemetry' ? 8 : 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      color: 'var(--accent, #6ea8fe)',
                      marginBottom: 2,
                    }}
                  >
                    {group === 'property' ? 'Properties' : 'Telemetries'}
                  </div>
                  {rows.map((f: TwinInfoField) => (
                    <div
                      key={`${f.source}:${f.label}`}
                      style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
                    >
                      <span style={{ opacity: 0.75 }}>{f.label}</span>
                      <span style={{ fontFamily: 'monospace', opacity: f.present ? 1 : 0.4 }}>
                        {f.value}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
});
